// src/components/Pages/Authentication/SignUp.js

import React from 'react';
import axios from 'axios';
import * as Yup from 'yup';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

import { Box, Button, Grid, Stack } from '@mui/material';
import { Typography, Link } from '@mui/material';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';

import ButtonLoading from '../../Controls/Buttons/ButtonLoading';
import { appURL, authURL, baseURL } from '../../Data/baseURL';
import CheckboxBasic from '../../Controls/FormsUI/CheckboxBasic';
import Copyright from '../../Apm/Other/Copyright';
import { initialValuesSignUp } from '../../Apm/Tables/InitialValues/initialValuesSignUp';
import { numbers_and_letters } from '../../Data/regex';
import TextFieldNormal from '../../Controls/Inputs/TextFieldNormal';
import MetaDecorator from '../../Apm/Other/MetaDecorator';
import signUp from '../../Data/MetaData/SignUp.json';
import { ContDePlata_Logo } from '../../Data/imageURL';

import AuthShell from '../../Public/AuthShell';

const validationSchema = Yup.object({
  email: Yup.string()
    .email(() => i18n.t('yup.invalid_email'))
    .required(() => i18n.t('yup.required')),
  password: Yup.string()
    .required(() => i18n.t('yup.required'))
    .min(8, () => i18n.t('yup.password_can_only_contain_latin_letters_numbers'))
    .matches(numbers_and_letters, () => i18n.t('yup.password_can_only_contain_latin_letters_numbers')),
  re_password: Yup.string()
    .oneOf([Yup.ref('password'), null], () => i18n.t('yup.password_must_match'))
    .required(() => i18n.t('yup.password_is_required')),
});

const axiosCleanInstance = axios.create({
  baseURL: baseURL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    accept: 'application/json',
  },
});

export default function SignUp() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [checkedTerms, setCheckedTerms] = React.useState(false);
  const [error, setError] = React.useState(false);

  const handleChange = () => {
    setCheckedTerms(!checkedTerms);
    setError(checkedTerms);
  };

  const handleError = () => {
    setError(true);
  };

  const handleSubmit = (values) => {
    setLoading(true);
    axiosCleanInstance
      .post(authURL, {
        username: values.email.trim().toLowerCase(),
        email: values.email.trim().toLowerCase(),
        password: values.password.trim(),
      })
      .then((res) => {
        if (res.status === 201) {
          setLoading(false);
          navigate('/', { replace: true });
          toast.success(
            () => (
              <div>
                <Typography variant="h6">{t('toast.user_successfully_created_part_1')}</Typography>
                <Typography variant="h6">
                  {t('toast.user_successfully_created_important')} {t('toast.user_successfully_created_part_2')}
                </Typography>
              </div>
            ),
            {
              autoClose: false,
              position: 'top-center',
              style: { marginTop: 75 },
            }
          );
        } else {
          toast.error(() => t('toast.user_not_created'), {
            autoClose: 5000,
          });
          setLoading(false);
        }
      })
      .catch((error) => {
        setLoading(false);
        if (
          error.response &&
          error.response.status &&
          (error.response.status === 400 || error.response.status === 403) &&
          error.response.data &&
          JSON.stringify(error.response.data).toString().includes('user with this email already exists')
        ) {
          toast.error(() => t('toast.user_with_this_email_already_exists'), {
            autoClose: 4000,
          });
        } else {
          toast.error(() => t('toast.user_not_created'), {
            autoClose: 4000,
          });
        }
      }, []);
  };

  return (
    <>
      <MetaDecorator
        description={signUp.pageDescriptionRo}
        title={signUp.pageTitle}
        imageUrl={ContDePlata_Logo}
        imageAlt={signUp.pageDescriptionRo}
        rel="/apps/signup"
      />
      <AuthShell mode="signup">
        {location.pathname === '/apps/signup' && (
          <Button component={NavLink} to="/" color="primary" size="small" variant="text">
            <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
              <Box sx={{ alignItems: 'flex-end' }}>
                <KeyboardBackspaceIcon />{' '}
              </Box>
              <Typography variant="button">{t('button.back_to_main_page')}</Typography>
            </Stack>
          </Button>
        )}

        <Typography align="center" variant="h5" sx={{ mt: 10, mb: 2 }}>
          {t('general.signup')}
        </Typography>

        <Formik enableReinitialize initialValues={initialValuesSignUp} validationSchema={validationSchema}>
          {({ values, validateForm, setTouched }) => {
            return (
              <Form>
                <Grid container item spacing={2}>
                  <Grid item xs={12}>
                    <TextFieldNormal
                      name="email"
                      variant="outlined"
                      required
                      fullWidth
                      label={t('general.email_address')}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextFieldNormal
                      name="password"
                      type="password"
                      variant="outlined"
                      required
                      fullWidth
                      label={t('general.password')}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextFieldNormal
                      name="re_password"
                      label={t('general.re_password')}
                      variant="outlined"
                      type="password"
                      required
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="primary">
                      {t('yup.password_can_only_contain_latin_letters_numbers')}
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container item xs={12} spacing={2} sx={{ mt: 1.7 }}>
                  <Grid item xs={12}>
                    <CheckboxBasic
                      checked={checkedTerms}
                      onChange={handleChange}
                      error={error}
                      errorMessage={error ? t('terms_and_conditions.please_accept_conditions') : undefined}
                      label={
                        <>
                          <Typography variant="body2">
                            {t('terms_and_conditions.accept_conditions_part_1')}
                            <Link href={`${appURL}/terms-conditions`} target="_blank" color="primary">
                              {t('terms_and_conditions.accept_conditions_part_2')}
                            </Link>
                          </Typography>
                        </>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <ButtonLoading
                      fullWidth
                      loading={loading}
                      variant="contained"
                      color="primary"
                      onClick={() =>
                        validateForm()
                          .then((errors) => {
                            if (Object.keys(errors).length > 0) {
                              toast.error(() => t('toast.correct_errors_on_page'));
                              return setTouched(errors);
                            } else if (!checkedTerms) {
                              handleError();
                            } else {
                              handleSubmit(values);
                            }
                          })
                          .catch((e) => new Error(e))
                      }
                    >
                      <Typography variant="button">{t('button.signup')}</Typography>
                    </ButtonLoading>
                  </Grid>
                </Grid>

                <Grid container justify="flex-end" sx={{ mt: 5 }}>
                  <Button component={NavLink} to="/apps/login" color="primary" size="small" variant="text">
                    <Typography variant="button">{t('button.already_have_account')}</Typography>
                  </Button>
                </Grid>
              </Form>
            );
          }}
        </Formik>

        {location.pathname === '/apps/signup' && (
          <Box sx={{ mt: 6, mb: 5 }}>
            <Copyright />
          </Box>
        )}
      </AuthShell>
    </>
  );
}
